import React, { useState, useEffect } from 'react';
import apiClient from '@/services/apiClient';
import { Wrench, Plus, X, Image as ImageIcon, Send } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const MyMaintenance: React.FC = () => {
    const [requests, setRequests] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    
    // Form State
    const [category, setCategory] = useState('Plumbing');
    const [description, setDescription] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchRequests();
    }, []);

    const fetchRequests = async () => {
        setLoading(true);
        try {
            const response = await apiClient.get('/tenants/maintenance');
            // Mock empty data handling depending on backend format
            setRequests(response.data || []);
        } catch (err: any) {
            console.error('Error fetching maintenance requests:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!description.trim()) {
            setError("Please provide a description");
            return;
        }
        
        setSubmitting(true);
        setError(null);
        try {
            const payload = { category, description };
            await apiClient.post('/tenants/maintenance', payload);
            
            // Reset and close
            setShowForm(false);
            setDescription('');
            setCategory('Plumbing');
            
            // Refresh list
            fetchRequests();
        } catch (err: any) {
            console.error('Submit maintenance error:', err);
            setError(err.response?.data?.error || 'Failed to submit request');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-6 relative">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-light-text dark:text-dark-text">Maintenance</h1>
                {!showForm && (
                     <button 
                        onClick={() => setShowForm(true)}
                        className="btn-primary flex items-center gap-2 py-2 px-4 shadow-sm"
                    >
                        <Plus size={18} />
                        Request Fix
                    </button>
                )}
            </div>

            <AnimatePresence>
                {showForm && (
                    <motion.div 
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                    >
                        <div className="bg-light-card dark:bg-dark-card rounded-xl shadow-sm border border-light-border dark:border-dark-border p-6 mb-6">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xl font-bold text-light-text dark:text-dark-text">New Request</h2>
                                <button onClick={() => setShowForm(false)} className="p-2 text-light-subtle dark:text-dark-subtle hover:bg-light-bg dark:hover:bg-dark-bg rounded-lg transition-colors">
                                    <X size={20} />
                                </button>
                            </div>
                            
                            {error && <div className="p-3 mb-4 text-sm text-red-600 bg-red-50 rounded-lg">{error}</div>}
                            
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-1">Category</label>
                                    <select 
                                        className="w-full px-4 py-3 rounded-xl border border-light-border dark:border-dark-border bg-light-bg dark:bg-dark-bg text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/50"
                                        value={category}
                                        onChange={(e) => setCategory(e.target.value)}
                                        disabled={submitting}
                                    >
                                        <option value="Plumbing">Plumbing</option>
                                        <option value="Electrical">Electrical</option>
                                        <option value="HVAC">Heating/Cooling</option>
                                        <option value="Appliance">Appliance</option>
                                        <option value="General">General Maintenance</option>
                                    </select>
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-1">Description</label>
                                    <textarea 
                                        className="w-full px-4 py-3 rounded-xl border border-light-border dark:border-dark-border bg-light-bg dark:bg-dark-bg text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/50 min-h-[120px]"
                                        placeholder="Describe the issue in detail..."
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        disabled={submitting}
                                    />
                                </div>
                                
                                <div className="flex justify-end gap-3 pt-2">
                                    <button 
                                        type="button" 
                                        onClick={() => setShowForm(false)}
                                        disabled={submitting}
                                        className="px-4 py-2 text-sm font-medium text-light-subtle dark:text-dark-subtle hover:bg-light-bg dark:hover:bg-dark-bg rounded-lg transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="submit" 
                                        disabled={submitting}
                                        className="btn-primary flex items-center gap-2 py-2 px-6"
                                    >
                                        {submitting ? 'Submitting...' : <><Send size={16} /> Submit</>}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="bg-light-card dark:bg-dark-card rounded-xl shadow-sm border border-light-border dark:border-dark-border p-6 min-h-[400px]">
                <h2 className="text-xl font-bold text-light-text dark:text-dark-text mb-6">Request History</h2>
                
                {loading ? (
                    <p className="text-center text-light-subtle dark:text-dark-subtle py-8">Loading requests...</p>
                ) : requests.length === 0 ? (
                    <div className="text-center py-12">
                        <Wrench size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                        <h3 className="text-lg font-medium text-light-text dark:text-dark-text mb-2">No maintenance requests</h3>
                        <p className="text-light-subtle dark:text-dark-subtle">You have not submitted any maintenance requests yet.</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {requests.map((req, i) => (
                            <div key={i} className="flex flex-col md:flex-row justify-between p-4 bg-light-bg dark:bg-dark-bg rounded-lg border border-light-border dark:border-dark-border gap-4">
                                <div className="flex gap-4">
                                    <div className="p-3 bg-primary/10 text-primary rounded-lg shrink-0 h-min">
                                        <Wrench size={24} />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-light-text dark:text-dark-text">{req.category || 'Maintenance'}</h4>
                                        <p className="text-sm text-light-subtle dark:text-dark-subtle mt-1">{req.description}</p>
                                        <p className="text-xs text-light-subtle dark:text-dark-subtle mt-2 flex items-center gap-1">
                                            {new Date(req.created_at || req.createdAt).toLocaleDateString()}
                                            {req.photos && req.photos.length > 0 && (
                                                <span className="flex items-center gap-1 ml-3 text-blue-500">
                                                    <ImageIcon size={12} /> {req.photos.length} photos
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                </div>
                                <div className="md:text-right shrink-0">
                                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold
                                        ${req.status === 'Completed' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                        req.status === 'In Progress' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                        'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'}
                                    `}>
                                        {req.status || 'Pending'}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
